import { useEffect, useState } from 'react';
import { Save, Info, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { supabase } from '../services/supabase';

interface Config {
  [key: string]: string;
}

const CONFIG_FIELDS = [
  { key: 'club_name', label: 'Nombre del club', type: 'text' },
  { key: 'club_slogan', label: 'Eslogan', type: 'text' },
  { key: 'contact_email', label: 'Email de contacto', type: 'email' },
  { key: 'app_url', label: 'URL de la app / web', type: 'text' },
  // `default_location` vivía aquí y NADIE lo leía: el formulario de grupos usaba
  // un literal en su propio código, no esta clave. Así que lo único que hacía era
  // ofrecer un ajuste que no ajustaba nada. El sitio de una sesión es su sede.
  { key: 'cancellation_hours_limit', label: 'Horas mínimas para cancelar sesión', type: 'number' },
  {
    key: 'default_bono_validity_days',
    label: 'Días de validez por defecto (bonos)',
    type: 'number',
  },
];

// `consentType` es la clave del LEDGER (`legal_acceptances.consent_type`), que
// NO es la clave de `app_config`. Son dos vocabularios distintos con una
// biyección 8↔8, y confundirlos es lo que hacía que el panel editara un
// documento que nadie enseñaba. Las RPC de la fase C hablan el del ledger.
const LEGAL_FIELDS = [
  {
    key: 'terms_conditions',
    consentType: 'terms',
    label: 'Términos y condiciones',
    hint: 'Obligatorio para todos los usuarios al registrarse.',
  },
  {
    key: 'privacy_policy',
    consentType: 'privacy',
    label: 'Política de privacidad (RGPD)',
    hint: 'Obligatorio para todos los usuarios al registrarse.',
  },
  // ⚠️ AQUÍ HABÍA UN SOLO CAMPO, `image_authorization`, Y DIRECCIÓN EDITABA UN
  // DOCUMENTO QUE NADIE ENSEÑABA.
  //
  // La mig 167 partió la autorización de imagen en dos —el club necesita el uso
  // interno para trabajar, y la difusión en redes es opcional y revocable por
  // separado— y desde entonces el alta pide cada una por su lado. El panel se
  // quedó editando el documento anterior al corte: lo que dirección escribiera
  // ahí no llegaba a ninguna pantalla.
  //
  // `image_authorization` NO se borra ni se toca: es el documento que firmaron
  // quienes se dieron de alta antes del corte, y el ledger apunta a esa versión.
  // Simplemente deja de ser editable desde aquí — un documento ya firmado no se
  // reescribe, se versiona.
  {
    key: 'image_internal_authorization',
    consentType: 'image_internal',
    label: 'Autorización de imagen · Uso interno',
    hint: 'Obligatoria para entrenar. Se firma en el alta de cualquier jugador.',
  },
  {
    key: 'image_diffusion_authorization',
    consentType: 'image_diffusion',
    label: 'Autorización de imagen · Difusión en redes',
    hint: 'Opcional y revocable. El jugador puede retirarla desde su perfil.',
  },
  {
    key: 'parental_authorization',
    consentType: 'parental',
    label: 'Autorización parental (menores)',
    hint: 'Solo se muestra al registrar alumnos menores de edad.',
  },
];

// [276] Lo que la app le enseña a quien va a pagar en mano. Vacío NO es un
// hueco de datos: es la señal de que esa forma de pago **no se ofrece**. Sin
// IBAN no se puede ofrecer una transferencia, así que la app no la ofrece, y
// cada opción aparece sola cuando dirección guarda su dato.
const PAYMENT_FIELDS = [
  {
    key: 'payment_bank_iban',
    label: 'IBAN para transferencias',
    placeholder: 'ES00 0000 0000 0000 0000 0000',
    hint: 'Sin esto, la app no ofrece pagar por transferencia.',
  },
  {
    key: 'payment_bank_holder',
    label: 'Titular de la cuenta',
    placeholder: 'VERTEX Performance S.L.',
    hint: 'A nombre de quién llega la transferencia. Se enseña junto al IBAN.',
  },
  {
    key: 'payment_bizum_phone',
    label: 'Teléfono para Bizum',
    placeholder: '600 000 000',
    hint: 'Sin esto, la app no ofrece pagar por Bizum.',
  },
  {
    key: 'payment_cash_instructions',
    label: 'Dónde y cuándo se paga en efectivo',
    placeholder: 'En las oficinas del club, de lunes a viernes de 17 a 20 h',
    hint: 'Sin esto, la app no ofrece pagar en efectivo.',
    textarea: true,
  },
];

const SOCIAL_FIELDS = [
  {
    key: 'social_instagram_url',
    label: 'Instagram',
    placeholder: 'https://instagram.com/vertexperformance',
  },
  {
    key: 'social_facebook_url',
    label: 'Facebook',
    placeholder: 'https://facebook.com/vertexperformance',
  },
  {
    key: 'social_linkedin_url',
    label: 'LinkedIn',
    placeholder: 'https://linkedin.com/company/vertexperformance',
  },
  {
    key: 'social_tiktok_url',
    label: 'TikTok',
    placeholder: 'https://tiktok.com/@vertexperformance',
  },
];

// Claves numéricas: se guardan como número JSON (jsonb number), NO como string.
const NUMERIC_KEYS = new Set(CONFIG_FIELDS.filter((f) => f.type === 'number').map((f) => f.key));

// Claves EDITABLES por el formulario. saveConfig solo escribe estas: así un Save
// no re-upserta (ni corrompe) claves que la página no muestra — p.ej.
// `audit_log_retention_days` o `stripe_mode`, que antes se guardaban de nuevo con
// tipo equivocado por iterar todo el config cargado.
//
// ⚠️ LOS TEXTOS LEGALES YA NO ESTÁN AQUÍ (fase C · C2).
//
// Antes sí: el bucle reescribía los 5 en CADA guardado, tocara dirección lo que
// tocara, y salía indemne solo porque el guard de la 270 tiene una puerta para
// el cuerpo idéntico (`NEW.value IS NOT DISTINCT FROM OLD.value`). Esa puerta
// sigue ahí y sigue haciendo falta —cualquier UPDATE que reescriba el mismo
// texto pasa por ella—, pero este bucle ya no la usa: en cuanto dirección
// editaba de verdad un texto legal, el mismo bucle chocaba con el guard y el
// botón "Guardar configuración" fallaba entero, dejando media configuración
// escrita.
//
// Un documento legal firmado no se guarda con el botón de guardar el eslogan.
// Cada texto tiene ahora su propia acción, y esa acción obliga a decir cuál de
// los dos casos de la doctrina de la 179 es.
const EDITABLE_KEYS = [
  ...CONFIG_FIELDS.map((f) => f.key),
  ...PAYMENT_FIELDS.map((f) => f.key),
  ...SOCIAL_FIELDS.map((f) => f.key),
];

// La siguiente versión que se propone al publicar. Las versiones son texto
// ('2', '3'), así que si alguna dejara de ser un número no se inventa nada: se
// deja vacío y lo escribe dirección.
function siguienteVersion(actual: string | undefined): string {
  if (!actual || !/^\d+$/.test(actual)) return '';
  return String(Number(actual) + 1);
}

type RpcResult = { ok?: boolean; error?: string } | null;

function LegalTextRow({
  campo,
  config,
  setConfig,
  original,
  version,
  onGuardado,
}: {
  campo: { key: string; consentType: string; label: string; hint: string };
  config: Config;
  setConfig: React.Dispatch<React.SetStateAction<Config>>;
  original: string;
  version: string | undefined;
  onGuardado: () => Promise<void>;
}) {
  const { key, consentType, label, hint } = campo;
  const [open, setOpen] = useState(false);
  // 'errata' | 'fondo'. Sin valor por defecto a propósito: elegir es el punto de
  // esta pantalla, y un default elige por dirección.
  const [modo, setModo] = useState<'errata' | 'fondo' | null>(null);
  const [motivo, setMotivo] = useState('');
  const [nuevaVersion, setNuevaVersion] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const cuerpo = config[key] ?? '';
  const modificado = cuerpo !== original;
  const relleno = !!cuerpo.trim();

  function abrirCierre() {
    const siguiente = !open;
    setOpen(siguiente);
    if (siguiente && !nuevaVersion) setNuevaVersion(siguienteVersion(version));
  }

  async function guardarTexto() {
    setError('');
    setOk('');

    if (!modificado) {
      setError('No has cambiado el texto.');
      return;
    }
    if (modo === null) {
      setError('Elige si es una errata o un cambio de fondo.');
      return;
    }

    setGuardando(true);
    try {
      // Los dos casos de la doctrina de la 179, cada uno por su puerta:
      //
      //   errata -> `fix_legal_text`: corrige en sitio, la versión NO se mueve
      //             y nadie tiene que volver a firmar. El cuerpo anterior queda
      //             íntegro en la auditoría.
      //
      //   fondo  -> `publish_legal_text`: publica una versión NUEVA, el cuerpo
      //             anterior se queda guardado en `legal_texts`, y marca
      //             `requiere_refirma` — que avisa, no bloquea (opción D).
      let data: unknown;
      let rpcError: { message: string } | null;

      if (modo === 'errata') {
        if (!motivo.trim()) {
          setError('El motivo es obligatorio: queda registrado con la corrección.');
          setGuardando(false);
          return;
        }
        ({ data, error: rpcError } = await supabase.rpc('fix_legal_text', {
          p_consent_type: consentType,
          p_body: cuerpo,
          p_reason: motivo.trim(),
        }));
      } else {
        if (!nuevaVersion.trim()) {
          setError('Escribe la versión nueva.');
          setGuardando(false);
          return;
        }
        ({ data, error: rpcError } = await supabase.rpc('publish_legal_text', {
          p_consent_type: consentType,
          p_body: cuerpo,
          p_version: nuevaVersion.trim(),
          // Un cambio de fondo obliga a volver a firmar: es lo que separa los
          // dos casos. Si no lo exigiera, sería una errata.
          p_requiere_refirma: true,
        }));
      }

      if (rpcError) {
        setError(`No se pudo guardar: ${rpcError.message}`);
        return;
      }
      // La RPC devuelve `{ok:false, error}` para lo que no es un fallo de base
      // (versión ya publicada, cuerpo vacío, motivo vacío). Sin esto el panel
      // diría que el texto legal se guardó cuando la base ha dicho que no.
      const res = data as RpcResult;
      if (!res?.ok) {
        setError(`No se pudo guardar: ${res?.error ?? 'respuesta inesperada'}`);
        return;
      }

      setOk(
        modo === 'errata'
          ? `Errata corregida. Sigue siendo la versión ${version ?? '—'}.`
          : `Publicada la versión ${nuevaVersion.trim()}. Quien firmó la anterior verá el aviso de re-firma.`
      );
      setMotivo('');
      setModo(null);
      await onGuardado();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="border-b border-bg-border last:border-b-0">
      <button
        onClick={abrirCierre}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-bg-elevated transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${relleno ? 'bg-success' : 'bg-error'}`}
          />
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-white font-inter">{label}</p>
              {/* La versión vigente, que hasta ahora no se veía en ninguna
                  pantalla: dirección editaba textos legales sin saber qué
                  versión estaba tocando. */}
              <span
                className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-bg-elevated text-text-secondary border border-bg-border"
                data-testid={`legal-version-${key}`}
              >
                {version ? `v${version}` : 'sin versión'}
              </span>
              {modificado && (
                <span className="text-[10px] font-inter text-warning">sin guardar</span>
              )}
            </div>
            <p className="text-text-secondary text-xs font-inter mt-0.5">{hint}</p>
          </div>
        </div>
        {open ? (
          <ChevronUp size={16} className="text-text-secondary flex-shrink-0" />
        ) : (
          <ChevronDown size={16} className="text-text-secondary flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-5 space-y-3">
          <textarea
            className="input resize-y font-inter text-xs leading-relaxed w-full"
            rows={12}
            placeholder="Escribe aquí el contenido completo del documento legal..."
            value={cuerpo}
            onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
            data-testid={`legal-body-${key}`}
          />
          <p className="text-text-secondary text-xs font-inter">{cuerpo.length} caracteres</p>

          {modificado && (
            <div className="rounded-lg border border-bg-border bg-bg-elevated p-4 space-y-3">
              <p className="text-xs font-inter text-white">
                Has cambiado el texto. ¿Qué clase de cambio es?
              </p>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`modo-${key}`}
                  className="mt-1"
                  checked={modo === 'errata'}
                  onChange={() => setModo('errata')}
                  data-testid={`legal-modo-errata-${key}`}
                />
                <span className="text-xs font-inter">
                  <span className="text-white">Errata</span>
                  <span className="text-text-secondary">
                    {' '}
                    — una tilde, un CIF, una razón social. El fondo no cambia:{' '}
                    <b>la versión se queda en v{version ?? '—'}</b> y nadie vuelve a firmar.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`modo-${key}`}
                  className="mt-1"
                  checked={modo === 'fondo'}
                  onChange={() => setModo('fondo')}
                  data-testid={`legal-modo-fondo-${key}`}
                />
                <span className="text-xs font-inter">
                  <span className="text-white">Cambio de fondo</span>
                  <span className="text-text-secondary">
                    {' '}
                    — cambia lo que la persona aceptó. <b>Sube la versión</b>, el texto anterior se
                    guarda, y a quien firmó el anterior se le avisa de que vuelva a firmar.
                  </span>
                </span>
              </label>

              {modo === 'errata' && (
                <div>
                  <label className="label">Motivo de la corrección</label>
                  <input
                    className="input"
                    placeholder="Ej.: faltaba una tilde en el artículo 3"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    data-testid={`legal-motivo-${key}`}
                  />
                  <p className="text-text-secondary text-xs mt-1 font-inter">
                    Obligatorio. Queda registrado con tu nombre, junto al texto anterior completo.
                  </p>
                </div>
              )}

              {modo === 'fondo' && (
                <div>
                  <label className="label">Versión nueva</label>
                  <input
                    className="input"
                    value={nuevaVersion}
                    onChange={(e) => setNuevaVersion(e.target.value)}
                    data-testid={`legal-version-nueva-${key}`}
                  />
                  <p className="text-text-secondary text-xs mt-1 font-inter">
                    La vigente es la v{version ?? '—'}. El texto de la v{version ?? '—'} no se
                    borra.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={guardarTexto}
                  disabled={guardando}
                  className="btn-gold flex items-center gap-2 text-xs"
                  data-testid={`legal-guardar-${key}`}
                >
                  <Save size={14} /> {guardando ? 'Guardando...' : 'Guardar este texto'}
                </button>
                {error && (
                  <p className="text-error text-xs font-inter" data-testid={`legal-error-${key}`}>
                    {error}
                  </p>
                )}
              </div>
            </div>
          )}

          {ok && (
            <p className="text-success text-xs font-inter" data-testid={`legal-ok-${key}`}>
              ✅ {ok}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function LegalTextsSection({
  config,
  setConfig,
  originales,
  versions,
  onGuardado,
}: {
  config: Config;
  setConfig: React.Dispatch<React.SetStateAction<Config>>;
  originales: Config;
  versions: Config;
  onGuardado: () => Promise<void>;
}) {
  return (
    <div className="card space-y-1 p-0 overflow-hidden">
      <div className="px-6 py-4 border-b border-bg-border">
        <h3 className="font-oswald text-sm uppercase tracking-wider text-white">Textos legales</h3>
        <p className="text-text-secondary text-xs mt-1 font-inter">
          Estos textos se muestran a los alumnos al registrarse en la app. Cada uno se guarda por su
          cuenta, con su propio botón: el de abajo, «Guardar configuración», no los toca.
        </p>
      </div>
      {LEGAL_FIELDS.map((campo) => (
        <LegalTextRow
          key={campo.key}
          campo={campo}
          config={config}
          setConfig={setConfig}
          original={originales[campo.key] ?? ''}
          version={versions[campo.key]}
          onGuardado={onGuardado}
        />
      ))}
    </div>
  );
}

export default function ConfiguracionPage() {
  const [config, setConfig] = useState<Config>({});
  // Lo cargado, sin tocar. Es lo que dice si un texto legal está modificado —y
  // por tanto si hay que preguntar errata o cambio de fondo— sin volver a la
  // base a preguntarlo.
  const [originales, setOriginales] = useState<Config>({});
  const [versions, setVersions] = useState<Config>({});
  const [loading, setLoading] = useState(true);
  // ⚠️ NO ES COSMÉTICO: AQUÍ SE PERDÍA CONFIGURACIÓN.
  //
  // Con el `error` descartado, una lectura caída dejaba `cfg` VACÍO y el
  // formulario se pintaba con todos los campos en blanco. Y `saveConfig`
  // recorre EDITABLE_KEYS enteras escribiendo `config[key] ?? ''`, así que
  // pulsar Guardar con la carga fallida **sobrescribía el nombre del club, el
  // eslogan y el email de contacto con cadenas vacías**. No es que no se vea
  // el dato: es que se pierde.
  //
  // Por eso el fallo no pinta un aviso al lado del formulario — no pinta el
  // formulario.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // El bucle de upserts no miraba ninguno de sus resultados y luego ponía
  // `saved` a true igual: el panel decía "✅ Guardado correctamente" sin haber
  // guardado. Es el mismo engaño que el "✓ Publicado" del planificador de PF.
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    setLoading(true);
    // `version` se LEE desde la fase C: hasta ahora el panel ni la pedía, así
    // que dirección editaba textos legales sin ver cuál estaba tocando.
    const { data, error: errorLectura } = await supabase
      .from('app_config')
      .select('key, value, version');
    if (errorLectura) {
      setErrorCarga(errorLectura.message || 'Error desconocido');
      setLoading(false);
      return;
    }
    setErrorCarga(null);
    const cfg: Config = {};
    const vers: Config = {};
    type AppConfigBrief = { key: string; value: unknown; version: string | null };
    ((data as AppConfigBrief[] | null) ?? []).forEach((row) => {
      const v = row.value;
      // Los valores ya se guardan con el tipo correcto (mig 171 + saveConfig):
      // string plano o número. Ya no hace falta el `.replace(/^"|"$/g,'')` que
      // compensaba el double-encoding; un texto legal que empezara por comilla
      // se mostraría bien.
      cfg[row.key] = typeof v === 'string' ? v : v == null ? '' : String(v);
      if (row.version) vers[row.key] = row.version;
    });
    setConfig(cfg);
    setOriginales(cfg);
    setVersions(vers);
    setLoading(false);
  }

  // Tras publicar o corregir un texto legal hay que releer su cuerpo y su
  // versión, pero NO recargar la página entera: `loadConfig` hace `setConfig`
  // con lo que hay en la base y se llevaría por delante lo que dirección
  // tuviera a medias en el eslogan o en el email de contacto. Solo se refrescan
  // las claves legales.
  async function recargarTextosLegales() {
    const { data, error: errorLectura } = await supabase
      .from('app_config')
      .select('key, value, version');
    // Si la relectura falla, NO se toca `config`: dejar los cuerpos legales en
    // blanco sobre el formulario es lo que luego se guarda. El texto que se
    // acaba de publicar está en la base; lo que no está es aquí.
    if (errorLectura) {
      setErrorCarga(
        `El texto se ha guardado, pero no se ha podido releer (${errorLectura.message}). Recarga la página antes de seguir editando.`
      );
      return;
    }
    type AppConfigBrief = { key: string; value: unknown; version: string | null };
    const filas = (data as AppConfigBrief[] | null) ?? [];
    const legales = new Set(LEGAL_FIELDS.map((f) => f.key));

    const cuerpos: Config = {};
    const vers: Config = {};
    filas.forEach((row) => {
      if (!legales.has(row.key)) return;
      const v = row.value;
      cuerpos[row.key] = typeof v === 'string' ? v : v == null ? '' : String(v);
      if (row.version) vers[row.key] = row.version;
    });

    setConfig((c) => ({ ...c, ...cuerpos }));
    setOriginales((o) => ({ ...o, ...cuerpos }));
    setVersions((v) => ({ ...v, ...vers }));
  }

  const legalesSinGuardar = LEGAL_FIELDS.filter(
    (f) => (config[f.key] ?? '') !== (originales[f.key] ?? '')
  );

  async function saveConfig() {
    setSaving(true);
    // Solo las claves editables del formulario (no todo `config`), con el TIPO
    // correcto: numéricas → número JSON (jsonb number); texto → string plano
    // (supabase-js ya serializa; NADA de JSON.stringify, que double-encodearía).
    //
    // Los textos legales NO están en EDITABLE_KEYS: van por su propia acción.
    for (const key of EDITABLE_KEYS) {
      const raw = config[key] ?? '';
      let value: string | number;
      if (NUMERIC_KEYS.has(key)) {
        const n = Number(raw);
        // Numérico vacío o no numérico → no lo escribas (conserva el valor en
        // BD; la columna value es NOT NULL).
        if (raw.trim() === '' || Number.isNaN(n)) continue;
        value = n;
      } else {
        value = raw;
      }
      const { error } = await supabase
        .from('app_config')
        .upsert({ key, value }, { onConflict: 'key' });
      // Se para en la primera que falle y se dice CUÁL: seguir el bucle
      // dejaría media configuración escrita y la otra media no, sin saber
      // dónde está el corte.
      if (error) {
        setSaveError(`No se pudo guardar "${key}": ${error.message}`);
        setSaving(false);
        return;
      }
    }
    setSaveError('');
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    setSaving(false);
    // Rehace la foto de "lo cargado" para las claves no legales; las legales no
    // las ha tocado este botón y siguen marcadas como sin guardar si lo están.
    setOriginales((o) => {
      const siguiente = { ...o };
      for (const key of EDITABLE_KEYS) siguiente[key] = config[key] ?? '';
      return siguiente;
    });
  }

  if (loading)
    return (
      <div className="flex justify-center h-64 items-center">
        <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
      </div>
    );

  // El formulario NO se pinta: un campo en blanco aquí es un campo que se
  // guarda en blanco.
  if (errorCarga)
    return (
      <div className="card max-w-3xl text-center py-12" data-testid="config-ilegible">
        <p className="text-error text-sm">{errorCarga}</p>
        <p className="text-text-secondary text-sm mt-2">
          No se muestra el formulario a propósito: con los campos en blanco, guardar borraría el
          nombre del club, el eslogan y el email de contacto.
        </p>
        <button className="btn-secondary mt-4" onClick={loadConfig} data-testid="config-reintentar">
          Reintentar
        </button>
      </div>
    );

  return (
    <div className="max-w-3xl space-y-8">
      {/* Config general */}
      <div className="card space-y-4">
        <h3 className="font-oswald text-sm uppercase tracking-wider text-white">Datos del club</h3>
        {CONFIG_FIELDS.map(({ key, label, type }) => (
          <div key={key}>
            <label className="label">{label}</label>
            <input
              type={type}
              className="input"
              value={config[key] ?? ''}
              onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      {/* [276] Cobros en mano */}
      <div className="card space-y-4">
        <h3 className="font-oswald text-sm uppercase tracking-wider text-white">Cobros en mano</h3>
        <p className="text-text-secondary text-xs">
          Lo que la app le enseña a quien elige pagar por transferencia, Bizum o en efectivo.{' '}
          <strong className="text-white">
            Un campo en blanco significa que esa forma de pago no se ofrece
          </strong>{' '}
          — no que se ofrezca sin datos. Estos datos los ve cualquier usuario con sesión iniciada.
        </p>
        {PAYMENT_FIELDS.map(({ key, label, placeholder, hint, textarea }) => (
          <div key={key}>
            <label className="label">{label}</label>
            {textarea ? (
              <textarea
                className="input min-h-20"
                data-testid={`config-${key}`}
                placeholder={placeholder}
                value={config[key] ?? ''}
                onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
              />
            ) : (
              <input
                type="text"
                className="input"
                data-testid={`config-${key}`}
                placeholder={placeholder}
                value={config[key] ?? ''}
                onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
              />
            )}
            <p className="text-text-secondary text-xs mt-1">{hint}</p>
          </div>
        ))}
      </div>

      {/* Redes sociales */}
      <div className="card space-y-4">
        <h3 className="font-oswald text-sm uppercase tracking-wider text-white">
          Redes sociales del club
        </h3>
        <p className="text-text-secondary text-xs">
          Las URLs configuradas se mostrarán como iconos clicables en la home de la app móvil. Deja
          en blanco para ocultar un icono.
        </p>
        {SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
          <div key={key}>
            <label className="label">{label}</label>
            <input
              type="url"
              className="input"
              placeholder={placeholder}
              value={config[key] ?? ''}
              onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      {/* Stripe */}
      <div className="card space-y-4">
        <h3 className="font-oswald text-sm uppercase tracking-wider text-white">
          Integración Stripe
        </h3>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-surface/50 border-border">
          <Info size={16} className="text-text-secondary shrink-0" />
          <div>
            <p className="text-sm font-medium text-white">
              Los cobros son <strong>reales</strong> siempre
            </p>
            <p className="text-text-secondary text-xs mt-0.5">
              Que Stripe cobre de verdad o no lo decide la clave secreta del servidor (
              <code>STRIPE_SECRET_KEY</code>), no esta pantalla. Aquí no hay nada que puedas cambiar
              que haga que un pago deje de ser real.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 text-xs text-text-secondary">
          <span className="shrink-0">{config.stripe_mode === 'live' ? '🟢' : '🟡'}</span>
          <p>
            <strong className="text-white">
              stripe_mode = {config.stripe_mode ?? '(sin valor)'}
            </strong>{' '}
            — es un ajuste heredado que solo habilita unas RPC de compra de prueba, hoy sin permiso
            para nadie. <strong>No influye en los cobros.</strong> Desde la migración 294 solo se
            cambia por migración, precisamente para que no pueda dar a entender otra cosa.
          </p>
        </div>
      </div>

      {/* Textos legales */}
      <LegalTextsSection
        config={config}
        setConfig={setConfig}
        originales={originales}
        versions={versions}
        onGuardado={recargarTextosLegales}
      />

      {/* Botón guardar */}
      <div className="space-y-3">
        {/* Antes, un texto legal editado se colaba en este botón y el guardado
            entero fallaba contra el guard de la 270. Ahora no se lleva nada
            suyo, así que hay que decirlo en voz alta o el cambio se quedaría en
            la pantalla creyendo que se guardó. */}
        {legalesSinGuardar.length > 0 && (
          <div
            className="flex items-start gap-2 p-3 rounded-lg border border-warning/30 bg-warning/10"
            data-testid="legales-sin-guardar"
          >
            <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-0.5" />
            <p className="text-warning text-xs font-inter">
              Tienes cambios sin guardar en {legalesSinGuardar.length}{' '}
              {legalesSinGuardar.length === 1 ? 'texto legal' : 'textos legales'} (
              {legalesSinGuardar.map((f) => f.label).join(', ')}). Este botón NO los guarda: cada
              texto se guarda con su propio botón, eligiendo errata o cambio de fondo.
            </p>
          </div>
        )}
        <div className="flex items-center gap-4">
          <button
            onClick={saveConfig}
            disabled={saving}
            className="btn-gold flex items-center gap-2"
          >
            <Save size={16} /> {saving ? 'Guardando...' : 'Guardar configuración'}
          </button>
          {saveError && (
            <p className="text-error text-sm font-inter" data-testid="config-save-error">
              {saveError}
            </p>
          )}
          {saved && (
            <p className="text-success text-sm font-inter animate-pulse">
              ✅ Guardado correctamente
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
